/**
 * Offers — the buyer+seller inbox for all open and closed negotiations.
 *
 * Each row shows: item snapshot, listed subtotal, current amount, whose turn,
 * round history (collapsed), and the action buttons for the party whose turn
 * it is. On mutual accept the row displays a link to the resulting PO in
 * /transactions.
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, imgUrl, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FoundingHat } from "@/components/FoundingHat";

const STATUS_UI = {
  pending_seller:  { label: "Waiting on seller", cls: "text-amber-300" },
  pending_buyer:   { label: "Waiting on buyer",  cls: "text-blue-300" },
  accepted:        { label: "Accepted → PO",     cls: "text-emerald-400" },
  declined:        { label: "Declined",          cls: "text-red-400" },
  cancelled:       { label: "Cancelled",         cls: "text-zinc-500" },
  expired:         { label: "Expired",           cls: "text-zinc-500" },
};

export default function Offers() {
  const { user } = useAuth();
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/offers/mine");
      setOffers(data.offers || []);
    } catch (err) {
      toast.error(formatErr(err));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="max-w-5xl mx-auto px-4 md:px-8 py-8 text-zinc-500">Loading offers…</div>;
  }

  const open = offers.filter((o) => ["pending_seller", "pending_buyer"].includes(o.status));
  const closed = offers.filter((o) => !["pending_seller", "pending_buyer"].includes(o.status));

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-8 py-6 md:py-10" data-testid="offers-page">
      <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">→ Offer Inbox</div>
      <h1 className="mt-1 font-display font-black uppercase text-3xl md:text-4xl tracking-tighter">Offers</h1>
      <p className="mt-2 text-sm text-zinc-400">
        Negotiate a price with a seller before opening a Purchase Order. When both parties agree,
        the PO opens automatically at the accepted amount and follows the normal flow.
      </p>

      {offers.length === 0 ? (
        <div className="mt-10 h57-surface p-6 text-center text-sm text-zinc-500" data-testid="offers-empty">
          You have no offers yet. Head to your Cart and click <span className="text-white">Make an Offer</span> to start one.
        </div>
      ) : (
        <>
          <Section title="Open" items={open} user={user} onAction={load} />
          <Section title="Closed" items={closed} user={user} onAction={load} />
        </>
      )}
    </div>
  );
}

function Section({ title, items, user, onAction }) {
  if (!items.length) return null;
  return (
    <div className="mt-8">
      <div className="font-mono text-[10px] uppercase tracking-widest text-[#FF5F1F] mb-3">→ {title} ({items.length})</div>
      <div className="space-y-4">
        {items.map((o) => (
          <OfferCard key={o.id} offer={o} user={user} onAction={onAction} />
        ))}
      </div>
    </div>
  );
}

function OfferCard({ offer, user, onAction }) {
  const iAmBuyer = offer.buyer_id === user?.id;
  const iAmSeller = offer.seller_id === user?.id;
  const other = iAmBuyer ? offer.seller_username : offer.buyer_username;
  const otherFounding = iAmBuyer ? offer.seller_founding_member : offer.buyer_founding_member;
  const status = STATUS_UI[offer.status] || { label: offer.status, cls: "" };
  const isMyTurn =
    (offer.status === "pending_seller" && iAmSeller) ||
    (offer.status === "pending_buyer" && iAmBuyer);
  const [counterAmount, setCounterAmount] = useState("");
  const [counterNote, setCounterNote] = useState("");
  const [showCounter, setShowCounter] = useState(false);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  async function call(path, body) {
    setBusy(true);
    try {
      const { data } = await api.post(`/offers/${offer.id}/${path}`, body || undefined);
      if (data.po_id) {
        toast.success(`Deal! PO opened for $${offer.current_amount.toFixed(2)}.`);
        setTimeout(() => nav("/transactions"), 400);
      } else if (path === "counter") {
        toast.success("Counter sent.");
      } else if (path === "accept") {
        toast.success("Accepted.");
      } else if (path === "decline") {
        toast.success("Declined.");
      } else if (path === "cancel") {
        toast.success("Offer cancelled.");
      }
      setShowCounter(false); setCounterAmount(""); setCounterNote("");
      onAction?.();
    } catch (err) {
      toast.error(formatErr(err));
    } finally { setBusy(false); }
  }

  return (
    <div className="h57-surface p-4" data-testid={`offer-card-${offer.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 inline-flex items-center gap-1">
            {iAmBuyer ? "Offer to" : "Offer from"} @{other}
            <FoundingHat founding={otherFounding} size={11} />
          </div>
          <div className="mt-1 font-display font-black uppercase tracking-tight text-lg">
            {offer.items_snapshot?.length === 1
              ? offer.items_snapshot[0]?.title
              : `${offer.items_snapshot?.length} cards`}
          </div>
          <div className="mt-1 text-xs text-zinc-500">
            Listed subtotal: <span className="text-white">${offer.original_subtotal?.toFixed(2)}</span> · Current offer:{" "}
            <span className="text-[#FF5F1F] font-semibold">${offer.current_amount?.toFixed(2)}</span>
          </div>
        </div>
        <div className={`font-mono text-[10px] uppercase tracking-widest ${status.cls}`}>{status.label}</div>
      </div>

      {/* Item snapshots row */}
      {offer.items_snapshot?.length > 0 && (
        <div className="mt-3 flex gap-2 flex-wrap">
          {offer.items_snapshot.map((it) => (
            <div key={it.listing_id} className="w-14 h-16 bg-black border border-white/10 flex-shrink-0"
                 title={`${it.title} · $${(it.listed_price || 0).toFixed(2)}`}>
              {it.front_image && (
                <img src={imgUrl(it.front_image, { w: 96 })} alt={it.title}
                     className="w-full h-full object-contain" loading="lazy" />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Round history */}
      {offer.rounds?.length > 1 && (
        <details className="mt-3">
          <summary className="cursor-pointer font-mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white">
            Round history ({offer.rounds.length})
          </summary>
          <ol className="mt-2 space-y-1 text-xs text-zinc-400">
            {offer.rounds.map((r, i) => (
              <li key={i} className="border-l border-white/10 pl-3">
                <span className="text-white uppercase font-semibold">{r.actor}</span> ·{" "}
                <span className="text-[#FF5F1F]">${r.amount?.toFixed(2)}</span>
                {r.accept && <span className="ml-1 text-emerald-400">accepted</span>}
                {r.note && <span className="ml-1 text-zinc-500">— &ldquo;{r.note}&rdquo;</span>}
                <span className="ml-1 text-[10px] text-zinc-600">{new Date(r.at).toLocaleString()}</span>
              </li>
            ))}
          </ol>
        </details>
      )}

      {/* Action row */}
      <div className="mt-4 flex items-center gap-2 flex-wrap">
        {offer.status === "accepted" && offer.po_id && (
          <Link to="/transactions" className="h57-btn-secondary text-xs" data-testid={`offer-po-link-${offer.id}`}>
            View Purchase Order →
          </Link>
        )}
        {isMyTurn && offer.status !== "accepted" && (
          <>
            <button
              onClick={() => call("accept")}
              disabled={busy}
              data-testid={`offer-accept-${offer.id}`}
              className="h57-btn-primary text-xs !py-2 !px-3"
            >
              Accept ${offer.current_amount?.toFixed(2)}
            </button>
            <button
              onClick={() => setShowCounter((v) => !v)}
              disabled={busy}
              data-testid={`offer-counter-toggle-${offer.id}`}
              className="h57-btn-secondary text-xs !py-2 !px-3"
            >
              Counter
            </button>
            <button
              onClick={() => window.confirm("Decline this offer? This closes the negotiation.") && call("decline")}
              disabled={busy}
              data-testid={`offer-decline-${offer.id}`}
              className="h57-btn-secondary text-xs !py-2 !px-3 !border-red-500/40 !text-red-400"
            >
              Decline
            </button>
          </>
        )}
        {iAmBuyer && ["pending_seller", "pending_buyer"].includes(offer.status) && (
          <button
            onClick={() => window.confirm("Withdraw this offer?") && call("cancel")}
            disabled={busy}
            data-testid={`offer-cancel-${offer.id}`}
            className="h57-btn-secondary text-xs !py-2 !px-3 !border-zinc-600 !text-zinc-400"
          >
            Withdraw
          </button>
        )}
      </div>

      {/* Counter form */}
      {showCounter && (
        <div className="mt-4 border-t border-white/10 pt-4 space-y-2" data-testid={`offer-counter-form-${offer.id}`}>
          <input
            type="number"
            step="0.01"
            min="0.01"
            className="h57-input"
            data-testid={`offer-counter-amount-${offer.id}`}
            value={counterAmount}
            onChange={(e) => setCounterAmount(e.target.value)}
            placeholder="Your counter (USD)"
          />
          <textarea
            className="h57-input min-h-[60px]"
            data-testid={`offer-counter-note-${offer.id}`}
            value={counterNote}
            onChange={(e) => setCounterNote(e.target.value)}
            placeholder="Optional note"
            maxLength={500}
          />
          <div className="flex justify-end gap-2">
            <button onClick={() => setShowCounter(false)} className="h57-btn-secondary text-xs">Cancel</button>
            <button
              onClick={() => call("counter", { amount: Number(counterAmount), note: counterNote.trim() || null })}
              disabled={busy || !(Number(counterAmount) > 0)}
              data-testid={`offer-counter-submit-${offer.id}`}
              className="h57-btn-primary text-xs disabled:opacity-40"
            >
              Send Counter →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
